import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormFieldError } from '@/components/ui/form-field-error';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useChurchStore } from '@/stores/church-store';
import { formatMoney } from '@/utils/format';

interface CatalogModule {
  code: string;
  name: string;
  description: string;
  group: string;
  acquisitionType: 'included' | 'addon';
  defaultEnabled: boolean;
  minimumPlan: 'trial' | 'community' | 'starter' | 'essential' | 'professional' | 'enterprise' | null;
  dependencies: string[];
  addonPriceMinor: number;
  addonPriceCurrency: string;
  enabled: boolean;
  availableOnPlan: boolean;
}

export const ModulesPage = () => {
  const { t } = useTranslation('modules');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);

  const catalogQuery = useQuery({
    queryKey: ['modules-catalog'],
    queryFn: () => api.get<{ data: CatalogModule[] }>('/modules/catalog'),
  });

  const [enableTarget, setEnableTarget] = useState<CatalogModule | null>(null);
  const [disableTarget, setDisableTarget] = useState<CatalogModule | null>(null);

  const modules = catalogQuery.data?.data ?? [];

  const { enabled, available, planGated } = useMemo(() => {
    const enabledList = modules.filter((module) => module.enabled);
    const availableList = modules.filter(
      (module) =>
        !module.enabled &&
        module.acquisitionType === 'addon' &&
        module.availableOnPlan,
    );
    const gatedList = modules.filter(
      (module) =>
        !module.enabled &&
        module.acquisitionType === 'addon' &&
        !module.availableOnPlan,
    );
    return { enabled: enabledList, available: availableList, planGated: gatedList };
  }, [modules]);

  const enable = useMutation({
    mutationFn: (code: string) => api.post('/modules/addons/enable', { code }),
    onSuccess: async (_data, code) => {
      toast.success(t('enable.success', { name: labelFor(code) }));
      setEnableTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['modules-catalog'] });
      await refreshCurrent();
    },
  });

  const disable = useMutation({
    mutationFn: (code: string) => api.delete(`/modules/addons/${code}`),
    onSuccess: async (_data, code) => {
      toast.success(t('disable.success', { name: labelFor(code) }));
      setDisableTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['modules-catalog'] });
      await refreshCurrent();
    },
  });

  const labelFor = (code: string) =>
    t(`catalog.${code}.name`, { defaultValue: code });

  const descriptionFor = (module: CatalogModule) =>
    t(`catalog.${module.code}.description`, { defaultValue: module.description });

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        tipId="settings.modules.enable"
      />

      <Section title={t('sections.enabled')}>
        <ul className="grid gap-2 sm:grid-cols-2">
          {enabled.map((module) => (
            <ModuleRow
              key={module.code}
              title={labelFor(module.code)}
              description={descriptionFor(module)}
              badges={
                <>
                  <Badge variant="brand">{t('badge.enabled')}</Badge>
                  <Badge>
                    {module.acquisitionType === 'included'
                      ? t('badge.included')
                      : t('badge.addon')}
                  </Badge>
                </>
              }
              action={
                module.acquisitionType === 'addon' ? (
                  <Button size="sm" variant="outline" onClick={() => setDisableTarget(module)}>
                    {t('actions.disable')}
                  </Button>
                ) : null
              }
            />
          ))}
        </ul>
      </Section>

      <Section title={t('sections.available')}>
        {available.length === 0 ? (
          <p className="text-body-sm text-fg-muted">{t('empty.available')}</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {available.map((module) => (
              <ModuleRow
                key={module.code}
                title={labelFor(module.code)}
                description={descriptionFor(module)}
                badges={<Badge>{t('badge.addon')}</Badge>}
                action={
                  <Button size="sm" onClick={() => setEnableTarget(module)}>
                    {t('actions.enable')}
                  </Button>
                }
              />
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('sections.planGated')}>
        {planGated.length === 0 ? (
          <p className="text-body-sm text-fg-muted">{t('empty.planGated')}</p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {planGated.map((module) => (
              <li key={module.code}>
                <PlanUpgradePrompt
                  title={labelFor(module.code)}
                  description={descriptionFor(module)}
                  planNote={t('enable.planNote', {
                    plan: t(`plan.${module.minimumPlan ?? 'professional'}`),
                  })}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <EnableDialog
        module={enableTarget}
        allModules={modules}
        loading={enable.isPending}
        error={
          enable.error instanceof ApiError
            ? tErrors(enable.error.code, enable.error.params)
            : undefined
        }
        onClose={() => setEnableTarget(null)}
        onConfirm={() => {
          if (enableTarget) enable.mutate(enableTarget.code);
        }}
      />

      <ConfirmDialog
        open={disableTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDisableTarget(null);
        }}
        title={t('disable.title', {
          name: disableTarget ? labelFor(disableTarget.code) : '',
        })}
        description={t('disable.description')}
        confirmLabel={t('disable.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={disable.isPending}
        onConfirm={() => {
          if (disableTarget) disable.mutate(disableTarget.code);
        }}
      />
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mb-8">
    <h2 className="mb-3 text-section-title font-semibold text-fg">{title}</h2>
    {children}
  </section>
);

const ModuleRow = ({
  title,
  description,
  badges,
  action,
}: {
  title: string;
  description: string;
  badges: ReactNode;
  action: ReactNode;
}) => (
  <li className="flex h-full flex-col gap-3 rounded-lg border border-border px-4 py-3 transition-colors hover:bg-surface-sunken">
    <div className="min-w-0 flex-1">
      <p className="font-medium text-fg">{title}</p>
      <p className="mt-1 text-body-sm text-fg-muted">{description}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">{badges}</div>
    </div>
    {action ? <div className="flex justify-end">{action}</div> : null}
  </li>
);

const EnableDialog = ({
  module,
  allModules,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  module: CatalogModule | null;
  allModules: CatalogModule[];
  loading: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('modules');
  const { t: tCommon } = useTranslation();

  if (!module) return null;

  const missingDeps = module.dependencies.filter(
    (code) => !allModules.find((entry) => entry.code === code)?.enabled,
  );

  const name = t(`catalog.${module.code}.name`, { defaultValue: module.name });
  const description = t(`catalog.${module.code}.description`, {
    defaultValue: module.description,
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('enable.title', { name })}</DialogTitle>
          <p className="text-body-sm text-fg-muted">{t('enable.description', { description })}</p>
        </DialogHeader>

        <div className="flex flex-col gap-2 text-body-sm text-fg-muted">
          {missingDeps.length > 0 ? (
            <p>
              {t('enable.dependencies', {
                list: missingDeps
                  .map((code) => t(`catalog.${code}.name`, { defaultValue: code }))
                  .join(', '),
              })}
            </p>
          ) : (
            <p>{t('enable.noDependencies')}</p>
          )}
          <p>
            {module.addonPriceMinor > 0
              ? t('enable.price', {
                  price: formatMoney(module.addonPriceMinor, module.addonPriceCurrency),
                })
              : t('enable.priceFree')}
          </p>
          <FormFieldError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {tCommon('action.cancel')}
          </Button>
          <Button loading={loading} onClick={onConfirm}>
            {t('enable.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
